import { useCatalogue } from "@/api/useCatalogue";
import { useMyBrandProfile } from "@/api/mutations";
import { KitHero } from "@/components/KitHero";
import { StockBadge } from "@/components/StockBadge";
import { Link } from "react-router";

/**
 * Catalogue — kit browser.
 *
 * Header treatment matches the new chrome: a quiet uppercase eyebrow (uses
 * the studio accent as a tiny signal — the only place the terracotta shows
 * on this page outside of an active sidebar item), generous whitespace,
 * and cards that lean on hairlines + hover-shadow instead of lifts.
 */
export function CataloguePage() {
  const { data: kits = [], isLoading } = useCatalogue();
  const { data: serverBrand } = useMyBrandProfile();

  return (
    <div className="mx-auto max-w-7xl px-8 py-10">
      <header className="mb-8 max-w-2xl">
        <div className="mb-2 flex items-center gap-2 text-eyebrow font-semibold uppercase text-fg-mute">
          <span
            aria-hidden
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ background: "var(--studio)" }}
          />
          Catalogue
        </div>
        <h1 className="mb-2 text-xl font-semibold leading-tight text-fg-1">
          Choose a test kit to brand
        </h1>
        <p className="text-base leading-relaxed text-fg-3">
          Pick a kit from the +CURA catalogue. You'll add your logo, choose
          colours, and preview every artefact before placing the order.
        </p>
      </header>

      {isLoading ? (
        <div className="text-fg-3">Loading catalogue…</div>
      ) : (
        <ul
          data-chrome
          className="grid list-none gap-5 p-0 sm:grid-cols-2 lg:grid-cols-3"
        >
          {kits.map((kit) => (
            <li key={kit.id}>
              <Link
                to={`/catalogue/${kit.id}`}
                className={[
                  "group flex h-full flex-col overflow-hidden rounded-lg border border-[var(--border-1)] bg-white",
                  "transition-shadow duration-150",
                  "hover:shadow-[0_2px_8px_-2px_rgba(33,43,50,0.08),0_8px_24px_-12px_rgba(33,43,50,0.12)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]",
                ].join(" ")}
              >
                <div className="aspect-[4/3] overflow-hidden border-b border-[var(--border-1)]">
                  <KitHero
                    brand={
                      serverBrand
                        ? {
                            accent: serverBrand.accent,
                            logoText: serverBrand.logoText,
                            tagline: serverBrand.tagline,
                            name: serverBrand.name,
                          }
                        : null
                    }
                    code={kit.code}
                    tube={kit.tube}
                    rhText={`For patients of ${serverBrand?.name ?? "your practice"}`}
                  />
                </div>
                <div className="flex flex-1 flex-col gap-2 p-5">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="text-base font-semibold text-fg-1">
                      {kit.title}
                    </h3>
                    <span className="font-mono text-xxs text-fg-mute">
                      {kit.code}
                    </span>
                  </div>
                  <div>
                    <StockBadge qty={kit.stockQty} />
                  </div>
                  <p className="text-sm leading-relaxed text-fg-3">
                    {kit.desc}
                  </p>
                  <div className="mt-auto flex items-baseline justify-between border-t border-[var(--border-1)] pt-3">
                    <span className="text-xs text-fg-3">
                      {kit.tube} · TAT {kit.tat}
                    </span>
                    <span className="text-base font-semibold tabular-nums text-fg-1">
                      £{kit.price.toFixed(2)}{" "}
                      <span className="text-xxs font-normal text-fg-mute">
                        / kit
                      </span>
                    </span>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
